import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { FormComponent, UiComponent } from 'components';
import { CheckBox } from 'components/form';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { useInfiniteScroll } from 'hooks';
import useModal from 'hooks/useModal';
import { EnrollQuery } from 'queries';
import * as M from 'styles/mobile';
import {
  AttendanceStatusType,
  getApplicationTypeDetailLabel,
  IEnroll,
} from 'types';
import AttendanceStatusModal from '../Detail/AttendanceStatusModal';
import NotificationModal from '../Detail/NotificationModal';
import {
  getAttendanceStatus,
  getSelectionStatus,
} from '../Detail/useVolunteerList';
import useDetail from '../useDetail';
import AttendeeSheet from './AttendeeSheet';
import * as S from './style';

const ATTENDANCE_TONE: Partial<Record<AttendanceStatusType, M.Tone>> = {
  PRESENT: 'success',
  LATE: 'info',
  ABSENT: 'error',
};

const getPhoneLast4 = (phone?: string) =>
  (phone ?? '').replace(/\D/g, '').slice(-4);

function Attendees() {
  const { id } = useParams<{ id: string }>();
  const { event } = useDetail();
  const { handleModal, handleClose } = useModal();
  const [name, setName] = useState<string>();
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [viewEnroll, setViewEnroll] = useState<IEnroll | null>(null);

  const eventStatus = event?.eventStatus;
  const isLottery = event?.recruitmentType === 'LOTTERY';
  const canNotify =
    eventStatus === 'RECRUITING' || eventStatus === 'RECRUITMENT_CLOSED';
  const canChangeAttendance = eventStatus === 'RECRUITMENT_CLOSED';

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
    EnrollQuery.useGetInfiniteByEvent(Number(id), name);
  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const { mutate: patchStatus, isPending } = EnrollQuery.usePatchStatus();

  const enrolls = useMemo(() => data ?? [], [data]);
  const checkedEnrolls = enrolls.filter((e) => checkedIds.includes(e.id));

  const handleCheck = (enrollId: number, checked: boolean) =>
    setCheckedIds((prev) =>
      checked ? [...prev, enrollId] : prev.filter((v) => v !== enrollId),
    );

  const handleSearch = ({ name: keyword }: { name?: string }) => {
    setName(keyword?.trim() || undefined);
    setCheckedIds([]);
  };

  const handleChangeAttendance = (attendanceStatus: AttendanceStatusType) =>
    patchStatus(
      { enrollIds: checkedIds, attendanceStatus },
      {
        onSuccess: () => {
          toast.success('출결 상태가 변경되었습니다.');
          setCheckedIds([]);
        },
        onError: () => toast.error(DEFAULT_ERROR_MESSAGE),
      },
    );

  const openAttendance = () =>
    handleModal(
      '출결상태 변경',
      <AttendanceStatusModal
        targets={checkedEnrolls}
        isPending={isPending}
        onSubmit={(status) => {
          handleChangeAttendance(status);
          handleClose();
        }}
      />,
      <></>,
    );

  const openNotification = () =>
    handleModal(
      '알림 전송',
      <NotificationModal targets={checkedEnrolls} />,
      <></>,
    );

  return (
    <>
      <FormComponent.Form css={S.search} submit={handleSearch}>
        <FormComponent.SearchInput
          name="name"
          placeholder="이름으로 검색해주세요"
        />
      </FormComponent.Form>
      <div css={M.list}>
        {enrolls.map((enroll) => {
          const {
            id: enrollId,
            user,
            attendanceStatus,
            applicationType,
            selectionStatus,
            enrollDate,
          } = enroll;
          const last4 = getPhoneLast4(user.phone);
          const meta = [
            getApplicationTypeDetailLabel(
              applicationType,
              event?.applicationTypeQuotaTitle,
              '',
            ),
            user.gender === 'MALE' ? '남' : '여',
            ...(isLottery && !applicationType
              ? [getSelectionStatus(selectionStatus)]
              : []),
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <div key={enrollId} css={S.attendeeRow}>
              <CheckBox
                checked={checkedIds.includes(enrollId)}
                disabled={attendanceStatus === 'CANCEL'}
                handleCheck={(checked) => handleCheck(enrollId, checked)}
              />
              <button
                type="button"
                css={M.listItem}
                onClick={() => setViewEnroll(enroll)}
              >
                <div css={M.listRow}>
                  <div css={S.nameWrap}>
                    <p css={S.name}>
                      {user.name}
                      {last4 && <span>({last4})</span>}
                    </p>
                    <span
                      css={M.tag(ATTENDANCE_TONE[attendanceStatus] ?? 'grey')}
                    >
                      {getAttendanceStatus(attendanceStatus)}
                    </span>
                  </div>
                  <span css={M.listDate}>
                    {dayjs(enrollDate).format('YYYY-MM-DD')}
                  </span>
                </div>
                {meta && <p css={S.meta}>{meta}</p>}
              </button>
            </div>
          );
        })}
        {!isLoading && enrolls.length === 0 && (
          <p css={M.empty}>참석자가 없습니다</p>
        )}
        {isFetchingNextPage && <p css={M.empty}>불러오는 중...</p>}
      </div>
      <div ref={sentinelRef} css={M.sentinel} />

      {checkedIds.length > 0 && (canNotify || canChangeAttendance) && (
        <>
          <div css={S.selectionBarSpacer} />
          <div css={S.selectionBar}>
            <p>{checkedIds.length}명 선택</p>
            {canNotify && (
              <UiComponent.Button
                variant="border"
                color="grey"
                onClick={openNotification}
              >
                알림 전송
              </UiComponent.Button>
            )}
            {canChangeAttendance && (
              <UiComponent.Button
                variant="contained"
                color="primary"
                onClick={openAttendance}
              >
                출결상태 변경
              </UiComponent.Button>
            )}
          </div>
        </>
      )}

      {viewEnroll && (
        <AttendeeSheet
          eventId={id || ''}
          enroll={viewEnroll}
          onClose={() => setViewEnroll(null)}
        />
      )}
    </>
  );
}

export default Attendees;
